import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Eye, EyeOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BlogTab({ language }) {
  const isLV = language === 'lv';
  const [target, setTarget] = useState('this');
  const [form, setForm] = useState({
    slug: '',
    title: '',
    content_html: '',
    metaDescription: '',
    heroImageUrl: '',
    status: 'publish',
    lang: 'lv',
    tags: '',
  });
  const [localPosts, setLocalPosts] = useState([]);
  const [prodPosts, setProdPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const loadPosts = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/blog`),
        axios.get(`${API}/admin/blog/production`),
      ]);
      setLocalPosts(Array.isArray(a.data) ? a.data : []);
      setProdPosts(Array.isArray(b.data?.posts) ? b.data.posts : []);
    } catch (e) {
      console.error('Blog load failed', e);
    }
  };

  useEffect(() => { loadPosts(); }, []);

  const slugify = (t) => (t || '')
    .replace(/[āĀ]/g, 'a').replace(/[čČ]/g, 'c').replace(/[ēĒ]/g, 'e')
    .replace(/[ģĢ]/g, 'g').replace(/[īĪ]/g, 'i').replace(/[ķĶ]/g, 'k')
    .replace(/[ļĻ]/g, 'l').replace(/[ņŅ]/g, 'n').replace(/[šŠ]/g, 's')
    .replace(/[ūŪ]/g, 'u').replace(/[žŽ]/g, 'z')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const onTitleChange = (v) => {
    setForm((f) => {
      const nextSlug = editingSlug || f.slug === slugify(f.title) || !f.slug ? slugify(v) : f.slug;
      return { ...f, title: v, slug: nextSlug };
    });
  };

  const reset = () => {
    setForm({ slug: '', title: '', content_html: '', metaDescription: '', heroImageUrl: '', status: 'publish', lang: 'lv', tags: '' });
    setEditingSlug(null);
    setResult(null);
    setError('');
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    setResult(null);
    if (!form.title.trim() || !form.content_html.trim()) {
      setError(isLV ? 'Virsraksts un saturs ir obligāti.' : 'Title and content are required.');
      return;
    }
    setLoading(true);
    try {
      const body = {
        slug: form.slug || undefined,
        title: form.title,
        content_html: form.content_html,
        metaDescription: form.metaDescription || null,
        heroImageUrl: form.heroImageUrl || null,
        status: form.status,
        lang: form.lang,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        target,
      };
      const { data } = await axios.post(`${API}/admin/blog/create`, body);
      setResult(data);
      await loadPosts();
    } catch (err) {
      setError(formatApiError(err, isLV ? 'Kļūda publicējot' : 'Publish failed'));
    } finally {
      setLoading(false);
    }
  };

  const loadIntoForm = (p) => {
    setEditingSlug(p.slug);
    setForm({
      slug: p.slug || '',
      title: p.title || '',
      content_html: p.content_html || '',
      metaDescription: p.meta_description || '',
      heroImageUrl: p.hero_image_url || '',
      status: p.status || 'publish',
      lang: p.lang || 'lv',
      tags: (p.tags || []).join(', '),
    });
    setResult(null);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deletePost = async (slug) => {
    if (!window.confirm(isLV ? `Dzēst ierakstu "${slug}"?` : `Delete post "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${slug}`);
      await loadPosts();
    } catch (err) {
      alert(formatApiError(err, 'Delete failed'));
    }
  };

  const toggleStatus = async (slug, current) => {
    const next = current === 'publish' ? 'draft' : 'publish';
    try {
      await axios.put(`${API}/admin/blog/${slug}/status`, { status: next });
      await loadPosts();
    } catch (err) {
      alert(formatApiError(err, 'Status change failed'));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-tab">
      {/* Form */}
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-outfit text-xl font-bold text-[#121212]">
              {editingSlug
                ? (isLV ? `Rediģēt: ${editingSlug}` : `Edit: ${editingSlug}`)
                : (isLV ? 'Publicēt jaunu blog ierakstu' : 'Publish new blog post')}
            </h2>
            <p className="text-sm text-[#5C5C5C] mt-1">
              {isLV
                ? 'Saturs tiek saglabāts izvēlētajā vidē. Production publicēšana izmantos kolv.lv BabyLoveGrowth API.'
                : 'Content is saved to the selected environment. Production publish relays via kolv.lv.'}
            </p>
          </div>
          {editingSlug && (
            <Button variant="outline" onClick={reset} data-testid="blog-reset-btn">
              {isLV ? 'Atcelt' : 'Cancel'}
            </Button>
          )}
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: form fields */}
          <div className="lg:col-span-2 space-y-3">
            <div>
              <label className="text-xs font-medium text-[#5C5C5C]">{isLV ? 'Virsraksts*' : 'Title*'}</label>
              <Input
                value={form.title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder={isLV ? 'Piem. Kāpēc pirkt vietīgi?' : 'e.g. Why buy local?'}
                data-testid="blog-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5C5C5C]">Slug</label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="kapec-pirkt-vietigi"
                  data-testid="blog-slug-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C5C]">{isLV ? 'Valoda' : 'Language'}</label>
                <select
                  className="w-full h-10 px-3 border border-[#E5E4E0] rounded-md bg-white text-sm"
                  value={form.lang}
                  onChange={(e) => setForm({ ...form, lang: e.target.value })}
                  data-testid="blog-lang-select"
                >
                  <option value="lv">Latviešu</option>
                  <option value="en">English</option>
                  <option value="ru">Русский</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C5C]">Meta description</label>
              <Input
                value={form.metaDescription}
                onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
                placeholder={isLV ? 'Īss apraksts SEO (līdz 160 simb.)' : 'Short SEO description (≤160 chars)'}
                data-testid="blog-meta-input"
                maxLength={300}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C5C]">Hero image URL</label>
              <Input
                value={form.heroImageUrl}
                onChange={(e) => setForm({ ...form, heroImageUrl: e.target.value })}
                placeholder="https://..."
                data-testid="blog-hero-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C5C]">Tags ({isLV ? 'atdalītas ar komatu' : 'comma-separated'})</label>
              <Input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="vietīgi, mājražotāji, latvija"
                data-testid="blog-tags-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-[#5C5C5C]">{isLV ? 'Saturs (HTML)*' : 'Content (HTML)*'}</label>
                <button
                  type="button"
                  onClick={() => setShowPreview((s) => !s)}
                  className="text-xs text-[#D97736] hover:underline"
                  data-testid="blog-preview-toggle"
                >
                  {showPreview ? (isLV ? 'Slēpt priekšskatījumu' : 'Hide preview') : (isLV ? 'Rādīt priekšskatījumu' : 'Show preview')}
                </button>
              </div>
              <Textarea
                rows={14}
                value={form.content_html}
                onChange={(e) => setForm({ ...form, content_html: e.target.value })}
                placeholder="<h2>Atbalsts vietējiem ražotājiem</h2>\n<p>...</p>"
                className="font-mono text-sm"
                data-testid="blog-content-input"
              />
              {showPreview && (
                <div
                  className="mt-2 prose prose-sm max-w-none p-4 bg-[#FAFAFA] border border-[#E5E4E0] rounded-md"
                  data-testid="blog-content-preview"
                  dangerouslySetInnerHTML={{ __html: form.content_html }}
                />
              )}
            </div>
          </div>

          {/* Right: target + publish controls */}
          <div className="space-y-3">
            <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-[#5C5C5C] block mb-2">{isLV ? 'Publicēt uz' : 'Publish to'}</label>
                <div className="space-y-2">
                  <label className={`flex items-start gap-2 p-2 rounded-md cursor-pointer border ${target === 'this' ? 'border-[#D97736] bg-white' : 'border-transparent hover:border-[#E5E4E0]'}`}>
                    <input
                      type="radio"
                      checked={target === 'this'}
                      onChange={() => setTarget('this')}
                      className="mt-1"
                      data-testid="blog-target-this"
                    />
                    <div>
                      <div className="text-sm font-medium text-[#121212]">{isLV ? 'Šī vide (Preview)' : 'This env (Preview)'}</div>
                      <div className="text-xs text-[#5C5C5C]">{isLV ? 'Tikai testēšanai' : 'For testing'}</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 p-2 rounded-md cursor-pointer border ${target === 'production' ? 'border-[#D97736] bg-white' : 'border-transparent hover:border-[#E5E4E0]'}`}>
                    <input
                      type="radio"
                      checked={target === 'production'}
                      onChange={() => setTarget('production')}
                      className="mt-1"
                      data-testid="blog-target-prod"
                    />
                    <div>
                      <div className="text-sm font-medium text-[#121212]">Production (kolv.lv)</div>
                      <div className="text-xs text-[#5C5C5C]">{isLV ? 'Publiskā vietne' : 'Public site'}</div>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[#5C5C5C]">Status</label>
                <select
                  className="w-full h-10 px-3 border border-[#E5E4E0] rounded-md bg-white text-sm"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  data-testid="blog-status-select"
                >
                  <option value="publish">{isLV ? 'Publicēts' : 'Published'}</option>
                  <option value="draft">{isLV ? 'Melnraksts' : 'Draft'}</option>
                </select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D97736] hover:bg-[#C06528] text-white"
                data-testid="blog-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingSlug ? (isLV ? 'Atjaunot' : 'Update') : (isLV ? 'Publicēt' : 'Publish'))}
              </Button>
            </div>

            {result && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm" data-testid="blog-result">
                <div className="font-medium text-emerald-900">
                  {result.action === 'created' ? (isLV ? '✓ Izveidots' : '✓ Created') : (isLV ? '✓ Atjaunināts' : '✓ Updated')}
                  {' '}— {result.target === 'production' ? 'Production' : 'Preview'}
                </div>
                <a href={result.permalink} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline break-all">
                  {result.permalink}
                </a>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700" data-testid="blog-error">
                {error}
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Two-column post lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BlogPostList
          title={isLV ? 'Šī vide (Preview)' : 'This env (Preview)'}
          badge="preview"
          posts={localPosts}
          isLV={isLV}
          onEdit={loadIntoForm}
          onDelete={deletePost}
          onToggleStatus={toggleStatus}
          dataTestId="blog-list-local"
        />
        <BlogPostList
          title="Production (kolv.lv)"
          badge="production"
          posts={prodPosts}
          isLV={isLV}
          readOnly
          dataTestId="blog-list-prod"
        />
      </div>
    </div>
  );
}

function BlogPostList({ title, badge, posts, isLV, onEdit, onDelete, onToggleStatus, readOnly, dataTestId }) {
  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4" data-testid={dataTestId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-outfit font-semibold text-[#121212]">{title}</h3>
        <span className={`text-xs px-2 py-0.5 rounded-full ${badge === 'production' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
          {posts.length} {isLV ? 'ieraksti' : 'posts'}
        </span>
      </div>
      {posts.length === 0 ? (
        <div className="text-sm text-[#5C5C5C] py-6 text-center">{isLV ? 'Vēl nav ierakstu' : 'No posts yet'}</div>
      ) : (
        <ul className="space-y-2">
          {posts.map((p) => (
            <li key={p.slug} className="border border-[#E5E4E0] rounded-md p-2.5 text-sm" data-testid={`blog-row-${p.slug}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#121212] truncate">{p.title}</div>
                  <div className="text-xs text-[#5C5C5C] truncate">/{p.slug} · {p.lang || 'lv'}{p.status === 'draft' ? ` · ${isLV ? 'melnraksts' : 'draft'}` : ''}</div>
                </div>
                {!readOnly && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => onToggleStatus(p.slug, p.status)}
                      className="px-2 py-1 text-xs rounded border border-[#E5E4E0] hover:border-[#D97736] hover:text-[#D97736]"
                      title={p.status === 'publish' ? (isLV ? 'Atsaukt' : 'Unpublish') : (isLV ? 'Publicēt' : 'Publish')}
                      data-testid={`blog-toggle-${p.slug}`}
                    >
                      {p.status === 'publish' ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => onEdit(p)}
                      className="px-2 py-1 text-xs rounded border border-[#E5E4E0] hover:border-[#D97736] hover:text-[#D97736]"
                      data-testid={`blog-edit-${p.slug}`}
                    >
                      {isLV ? 'Rediģēt' : 'Edit'}
                    </button>
                    <button
                      onClick={() => onDelete(p.slug)}
                      className="px-2 py-1 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`blog-delete-${p.slug}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default BlogTab;
